import { Controller } from "@hotwired/stimulus";

const VIEW_KEY = "markup-bands-view";
const WIDTH = 640;
const HEIGHT = 240;
const PAD = { top: 28, right: 36, bottom: 32, left: 44 };
const SVG = "http://www.w3.org/2000/svg";

export default class extends Controller {
  static targets = ["rows", "template", "chart", "sliders", "viewButton", "trial", "trialResult"];
  static values = { bands: Array, partCode: String, labels: Object };

  connect() {
    const bands = this.bandsValue.length ? this.bandsValue : [{ min_price: 0, percent: 0 }];
    bands.forEach((band) => this.appendRow(band));
    this.show(this.storedView());
  }

  get rowElements() {
    return Array.from(this.rowsTarget.querySelectorAll(".markup-band"));
  }

  get bands() {
    return this.rowElements
      .map((row) => ({ row, min: Number(row.querySelector("[data-field=min]").value || 0), percent: Number(row.querySelector("[data-field=percent]").value || 0) }))
      .sort((a, b) => a.min - b.min);
  }

  appendRow(band) {
    const row = document.createElement("div");
    row.className = "markup-band";
    row.innerHTML = this.templateTarget.innerHTML;
    row.querySelector("[data-field=code]").value = this.partCodeValue;
    row.querySelector("[data-field=min]").value = Number(band.min_price);
    row.querySelector("[data-field=percent]").value = Number(band.percent);
    row.querySelector("[data-field=slider]").value = Number(band.percent);
    this.rowsTarget.appendChild(row);
    this.refresh();
  }

  add() {
    const bands = this.bands;
    const last = bands[bands.length - 1];
    const min = last ? Math.max(last.min * 2, last.min + 100) : 0;
    this.appendRow({ min_price: min, percent: last ? last.percent : 0 });
  }

  remove(event) {
    if (this.rowElements.length === 1) return;
    event.currentTarget.closest(".markup-band").remove();
    this.refresh();
  }

  slide(event) {
    const row = event.currentTarget.closest(".markup-band");
    row.querySelector("[data-field=percent]").value = event.currentTarget.value;
    this.refresh();
  }

  type(event) {
    const row = event.currentTarget.closest(".markup-band");
    row.querySelector("[data-field=slider]").value = row.querySelector("[data-field=percent]").value;
    this.refresh();
  }

  sort() {
    this.bands.forEach(({ row }) => this.rowsTarget.appendChild(row));
    this.refresh();
  }

  switch(event) {
    const view = event.currentTarget.dataset.view;
    try { localStorage.setItem(VIEW_KEY, view); } catch (error) {}
    document.querySelectorAll("[data-controller~='markup-bands']").forEach((editor) => {
      const controller = this.application.getControllerForElementAndIdentifier(editor, "markup-bands");
      controller?.show(view);
    });
  }

  storedView() {
    try { return localStorage.getItem(VIEW_KEY) || "sliders"; } catch (error) { return "sliders"; }
  }

  show(view) {
    this.slidersTarget.classList.toggle("markup-bands__sliders--compact", view === "chart");
    this.chartTarget.hidden = view !== "chart";
    this.viewButtonTargets.forEach((button) => button.setAttribute("aria-pressed", button.dataset.view === view));
    this.draw();
  }

  refresh() {
    const bands = this.bands;
    bands.forEach(({ row, min }, index) => {
      const next = bands[index + 1];
      row.querySelector(".markup-band__range").textContent = next ? this.label("range_to", { price: this.money(next.min) }) : this.label("and_up");
      row.querySelector(".markup-band__example").textContent = this.example(min || (next ? next.min / 2 : 100), bands[index].percent);
    });
    this.trial();
    this.draw();
  }

  example(price, percent) {
    return `${this.money(price)} → ${this.money(price * (1 + percent / 100))}`;
  }

  trial() {
    if (!this.hasTrialTarget) return;
    const price = Number(this.trialTarget.value || 0);
    const band = this.bands.filter((item) => item.min <= price).pop();
    const percent = band ? band.percent : 0;
    this.trialResultTarget.textContent = this.label("trial_result", { percent, price: this.money(price * (1 + percent / 100)) });
  }

  label(key, replacements = {}) {
    return Object.entries(replacements).reduce((text, [name, value]) => text.replace(`%{${name}}`, value), this.labelsValue[key]);
  }

  money(amount) {
    const rounded = Math.round(amount * 100) / 100;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: rounded % 1 ? 2 : 0 }).format(rounded);
  }

  draw() {
    if (this.chartTarget.hidden) return;
    const bands = this.bands;
    const top = Math.max(...bands.map((band) => band.min), 100);
    this.maxPrice = top * 1.6;
    this.maxPercent = Math.max(100, ...bands.map((band) => band.percent * 1.25));

    const svg = document.createElementNS(SVG, "svg");
    svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
    svg.setAttribute("class", "markup-chart");
    svg.setAttribute("role", "img");

    this.axes(svg);
    bands.forEach((band, index) => {
      const next = bands[index + 1];
      const x1 = this.x(band.min);
      const x2 = next ? this.x(next.min) : WIDTH - PAD.right;
      const y = this.y(band.percent);

      svg.appendChild(this.shape("rect", { x: x1, y, width: Math.max(x2 - x1, 1), height: HEIGHT - PAD.bottom - y, class: "markup-chart__area" }));
      if (next) svg.appendChild(this.shape("line", { x1: x2, y1: y, x2, y2: this.y(next.percent), class: "markup-chart__riser" }));
      svg.appendChild(this.shape("line", { x1, y1: y, x2, y2: y, class: "markup-chart__step" }));

      const label = this.shape("text", { x: (x1 + x2) / 2, y: y - 10, class: "markup-chart__label" });
      label.textContent = `${band.percent}%`;
      svg.appendChild(label);

      const level = this.shape("circle", { cx: (x1 + x2) / 2, cy: y, r: 7, class: "markup-chart__handle", tabindex: 0 });
      level.addEventListener("pointerdown", (event) => this.drag(event, band.row, "percent"));
      level.addEventListener("keydown", (event) => this.nudge(event, band.row, "percent", event.key === "ArrowUp" ? 1 : event.key === "ArrowDown" ? -1 : 0));
      svg.appendChild(level);

      if (index > 0) {
        const edge = this.shape("rect", { x: x1 - 5, y: HEIGHT - PAD.bottom - 5, width: 10, height: 10, rx: 2, class: "markup-chart__edge", tabindex: 0 });
        edge.addEventListener("pointerdown", (event) => this.drag(event, band.row, "min"));
        edge.addEventListener("keydown", (event) => this.nudge(event, band.row, "min", event.key === "ArrowRight" ? 5 : event.key === "ArrowLeft" ? -5 : 0));
        svg.appendChild(edge);
      }
    });

    this.chartTarget.replaceChildren(svg);
    this.svg = svg;
  }

  axes(svg) {
    svg.appendChild(this.shape("line", { x1: PAD.left, y1: HEIGHT - PAD.bottom, x2: WIDTH - PAD.right, y2: HEIGHT - PAD.bottom, class: "markup-chart__axis" }));
    [0, 0.25, 0.5, 0.75, 1].forEach((share) => {
      const percent = Math.round(this.maxPercent * share);
      const y = this.y(percent);
      svg.appendChild(this.shape("line", { x1: PAD.left, y1: y, x2: WIDTH - PAD.right, y2: y, class: "markup-chart__grid" }));
      const tick = this.shape("text", { x: PAD.left - 8, y: y + 4, class: "markup-chart__tick markup-chart__tick--y" });
      tick.textContent = `${percent}%`;
      svg.appendChild(tick);
    });
    [0, 0.1, 0.25, 0.5, 1].forEach((share) => {
      const price = Math.round(this.maxPrice * share);
      const tick = this.shape("text", { x: this.x(price), y: HEIGHT - PAD.bottom + 20, class: "markup-chart__tick" });
      tick.textContent = this.money(price);
      svg.appendChild(tick);
    });
  }

  shape(name, attributes) {
    const element = document.createElementNS(SVG, name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    return element;
  }

  x(price) {
    return PAD.left + Math.sqrt(Math.max(price, 0) / this.maxPrice) * (WIDTH - PAD.left - PAD.right);
  }

  y(percent) {
    return HEIGHT - PAD.bottom - (percent / this.maxPercent) * (HEIGHT - PAD.top - PAD.bottom);
  }

  priceAt(x) {
    const share = Math.min(Math.max((x - PAD.left) / (WIDTH - PAD.left - PAD.right), 0), 1);
    return share * share * this.maxPrice;
  }

  percentAt(y) {
    const share = (HEIGHT - PAD.bottom - y) / (HEIGHT - PAD.top - PAD.bottom);
    return Math.min(Math.max(share * this.maxPercent, 0), this.maxPercent);
  }

  drag(event, row, field) {
    event.preventDefault();
    const move = (moveEvent) => {
      const point = this.svg.createSVGPoint();
      point.x = moveEvent.clientX;
      point.y = moveEvent.clientY;
      const local = point.matrixTransform(this.svg.getScreenCTM().inverse());
      const value = field === "percent" ? Math.round(this.percentAt(local.y)) : this.roundPrice(this.priceAt(local.x));
      this.setField(row, field, value);
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      this.sort();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop, { once: true });
  }

  nudge(event, row, field, step) {
    if (!step) return;
    event.preventDefault();
    const input = row.querySelector(`[data-field=${field}]`);
    this.setField(row, field, Math.max(Number(input.value || 0) + step, 0));
    this.sort();
    const index = this.bands.findIndex((band) => band.row === row);
    const handles = this.chartTarget.querySelectorAll(field === "percent" ? ".markup-chart__handle" : ".markup-chart__edge");
    handles[field === "percent" ? index : index - 1]?.focus();
  }

  roundPrice(price) {
    const step = price < 100 ? 5 : price < 1000 ? 25 : 100;
    return Math.round(price / step) * step;
  }

  setField(row, field, value) {
    row.querySelector(`[data-field=${field}]`).value = value;
    if (field === "percent") row.querySelector("[data-field=slider]").value = value;
    this.refresh();
  }
}
